import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  AppBar,
  Avatar,
  Box,
  Button,
  Container,
  Grid,
  Stack,
  TextField,
  Toolbar,
  Typography,
} from '@mui/material';
import {
  AccessTimeRounded,
  CalendarMonthOutlined,
  FavoriteBorder,
  GroupsOutlined,
  KeyboardArrowLeftRounded,
  KeyboardArrowRightRounded,
  KeyboardArrowDownRounded,
  MonitorHeartOutlined,
  NotificationsNoneOutlined,
  OpacityOutlined,
  SpaOutlined,
  TimelineRounded,
  TrendingUpRounded,
  WaterDropOutlined,
} from '@mui/icons-material';
import { motion, useReducedMotion, useScroll, useTransform } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import { waitlistAPI } from '../services/api';
import BrandLogo from '../components/BrandLogo';
import { tokens } from '../theme';
import fastTimeAppIcon from '../assets/fasttime-app-icon.png';
import landingForest from '../assets/landing-forest.png';
import cardMountains from '../assets/card-mountains.png';
import cardCommunity from '../assets/card-community.png';
import ctaBackground from '../assets/cta-background.png';

const MotionBox = motion.create(Box);
const MotionTypography = motion.create(Typography);

const sectionAnimation = {
  hidden: { opacity: 0, y: 42, scale: 0.985 },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: { duration: 0.9, ease: [0.22, 1, 0.36, 1] },
  },
};

const heroHighlights = [
  { value: '4', label: 'Fasting protocols' },
  { value: '7-day', label: 'Weekly summaries' },
  { value: 'Calories + macros', label: 'Food logging' },
];

const featureStories = [
  {
    title: 'Stay focused on the fast you are in',
    copy:
      'See your timer, streak, and daily goals at a glance, then log water, steps, and exercise in a few quick taps.',
    accent: 'rgba(169, 191, 122, 0.16)',
    image: landingForest,
    stats: ['16:8, 18:6, 20:4, 24h', 'Active countdown', 'Water, steps, exercise'],
  },
  {
    title: 'Food logging that stays easy to follow',
    copy:
      'Track meals, calories, and macros in one place, with recent foods ready when you want to log faster.',
    accent: 'rgba(201, 139, 103, 0.14)',
    image: cardMountains,
    stats: ['Breakfast / lunch / dinner / snacks', 'Recent foods', 'Macro progress bars'],
  },
  {
    title: 'Progress and account tools stay close by',
    copy:
      'Review weekly progress, update your preferences, and manage billing and security without leaving the app.',
    accent: 'rgba(123, 196, 142, 0.12)',
    image: cardCommunity,
    stats: ['Weekly charts', 'Profile + reminders', 'Store subscription sync'],
  },
];

const dailyFlow = [
  {
    title: 'Choose a fasting protocol',
    copy: 'Pick a fasting window and see your countdown, planned end time, and streak right away.',
    icon: <AccessTimeRounded sx={{ color: tokens.accentStrong }} />,
  },
  {
    title: 'Log the day as it happens',
    copy: 'Update water, steps, exercise, and meals as you go, with totals that stay current.',
    icon: <WaterDropOutlined sx={{ color: tokens.accentStrong }} />,
  },
  {
    title: 'Review the week in one place',
    copy: 'See weekly trends, fasting history, and key stats in one clean view.',
    icon: <TimelineRounded sx={{ color: tokens.accentStrong }} />,
  },
];

const dashboardTiles = [
  {
    title: 'Current fast',
    value: '16:32',
    detail: '16:8 protocol active',
    icon: <SpaOutlined sx={{ color: tokens.accentStrong }} />,
  },
  {
    title: 'Hydration',
    value: '1.6 / 2.5L',
    detail: 'Water goal in progress',
    icon: <OpacityOutlined sx={{ color: tokens.accentStrong }} />,
  },
  {
    title: 'Calories',
    value: '1,480 / 2,000',
    detail: 'Macro targets update with meals',
    icon: <TrendingUpRounded sx={{ color: tokens.accentStrong }} />,
  },
  {
    title: 'This week',
    value: '5 / 7 fasted',
    detail: 'Weekly summary at a glance',
    icon: <GroupsOutlined sx={{ color: tokens.accentStrong }} />,
  },
];

const screenshotSlides = [
  {
    id: 'home',
    title: 'Home',
    blurb: 'Fast timer, hydration, streaks, and quick daily logging.',
    accent: 'rgba(169,191,122,0.18)',
    image: landingForest,
    eyebrow: 'TODAY',
    render: 'home',
  },
  {
    id: 'food',
    title: 'Food Log',
    blurb: 'Calories, macros, meal groups, and recent foods in one flow.',
    accent: 'rgba(201,139,103,0.18)',
    image: cardMountains,
    eyebrow: 'NUTRITION',
    render: 'food',
  },
  {
    id: 'progress',
    title: 'Progress',
    blurb: 'Weekly trends for fasting, steps, calories, and weight.',
    accent: 'rgba(123,196,142,0.16)',
    image: ctaBackground,
    eyebrow: 'WEEKLY VIEW',
    render: 'progress',
  },
  {
    id: 'protocols',
    title: 'Protocols',
    blurb: 'Choose from 16:8, 18:6, 20:4, and 24h fasting windows.',
    accent: 'rgba(150,170,255,0.16)',
    image: landingForest,
    eyebrow: 'START A FAST',
    render: 'protocols',
  },
  {
    id: 'settings',
    title: 'Settings',
    blurb: 'Profile, reminders, units, and preferences stay easy to manage.',
    accent: 'rgba(196,170,255,0.16)',
    image: cardCommunity,
    eyebrow: 'PREFERENCES',
    render: 'settings',
  },
  {
    id: 'billing',
    title: 'Billing',
    blurb: 'Subscription status and store-managed billing in one place.',
    accent: 'rgba(255,210,140,0.16)',
    image: cardMountains,
    eyebrow: 'PREMIUM',
    render: 'billing',
  },
];

function FadeSection({ children, ...sxProps }) {
  return (
    <MotionBox
      variants={sectionAnimation}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, amount: 0.2 }}
      {...sxProps}
    >
      {children}
    </MotionBox>
  );
}

function GlassPanel({ children, sx }) {
  return (
    <Box
      sx={{
        border: `1px solid ${tokens.border}`,
        background: 'linear-gradient(180deg, rgba(18,32,25,0.92) 0%, rgba(12,21,17,0.96) 100%)',
        backdropFilter: 'blur(18px)',
        borderRadius: 3,
        boxShadow: '0 30px 90px rgba(0,0,0,0.28)',
        ...sx,
      }}
    >
      {children}
    </Box>
  );
}

function PhoneMockup({ children, tilt = 0, accent = 'rgba(169,191,122,0.16)', sx }) {
  return (
    <MotionBox
      whileHover={{ y: -8, rotate: tilt * 0.35 }}
      animate={{ y: [0, -10, 0] }}
      transition={{ duration: 6.5, repeat: Infinity, ease: 'easeInOut' }}
      sx={{
        position: 'relative',
        mx: 'auto',
        width: '100%',
        maxWidth: 296,
        p: '5px',
        borderRadius: '35px',
        border: '1px solid rgba(255,255,255,0.14)',
        background: 'linear-gradient(180deg, #3e4743 0%, #1b221f 16%, #0a110e 55%, #2a312e 100%)',
        boxShadow: '0 30px 76px rgba(0,0,0,0.3), inset 0 1px 0 rgba(255,255,255,0.14), inset 0 -1px 0 rgba(255,255,255,0.06)',
        transform: `rotate(${tilt}deg)`,
        ...sx,
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          inset: '10% -16% auto',
          height: 152,
          borderRadius: 6,
          background: `radial-gradient(circle, ${accent} 0%, rgba(0,0,0,0) 70%)`,
          pointerEvents: 'none',
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          left: '-3px',
          top: 106,
          width: '3px',
          height: 30,
          borderRadius: '3px 0 0 3px',
          background: 'linear-gradient(180deg, rgba(255,255,255,0.32), rgba(255,255,255,0.08))',
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          left: '-3px',
          top: 148,
          width: '3px',
          height: 52,
          borderRadius: '3px 0 0 3px',
          background: 'linear-gradient(180deg, rgba(255,255,255,0.28), rgba(255,255,255,0.08))',
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          left: '-3px',
          top: 212,
          width: '3px',
          height: 52,
          borderRadius: '3px 0 0 3px',
          background: 'linear-gradient(180deg, rgba(255,255,255,0.28), rgba(255,255,255,0.08))',
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          right: '-3px',
          top: 164,
          width: '3px',
          height: 74,
          borderRadius: '0 3px 3px 0',
          background: 'linear-gradient(180deg, rgba(255,255,255,0.3), rgba(255,255,255,0.08))',
        }}
      />
      <Box
        sx={{
          position: 'relative',
          overflow: 'hidden',
          borderRadius: '30px',
          border: '1px solid rgba(255,255,255,0.07)',
          bgcolor: '#09110d',
          minHeight: 556,
          boxShadow: 'inset 0 0 0 1px rgba(255,255,255,0.03)',
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: 7,
            left: '50%',
            transform: 'translateX(-50%)',
            width: 94,
            height: 20,
            borderRadius: '12px',
            bgcolor: '#020403',
            zIndex: 3,
            boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.05)',
          }}
        >
          <Box
            sx={{
              position: 'absolute',
              top: 6,
              left: 15,
              width: 34,
              height: 4,
              borderRadius: 999,
              bgcolor: 'rgba(255,255,255,0.08)',
            }}
          />
          <Box
            sx={{
              position: 'absolute',
              top: 4,
              right: 12,
              width: 10,
              height: 10,
              borderRadius: '50%',
              bgcolor: '#101815',
              border: '1px solid rgba(255,255,255,0.04)',
            }}
          />
        </Box>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{
            px: 2.1,
            pt: 1.25,
            pb: 0.9,
            color: 'rgba(243,236,223,0.72)',
            fontSize: '0.72rem',
          }}
        >
          <Typography sx={{ fontSize: '0.72rem' }}>9:41</Typography>
          <Box sx={{ width: 72, height: 6 }} />
          <Typography sx={{ fontSize: '0.72rem' }}>100%</Typography>
        </Stack>
        <Box sx={{ px: 1.1, pb: 1.15 }}>
          {children}
        </Box>
      </Box>
    </MotionBox>
  );
}

function ScreenshotSlide({ slide }) {
  if (slide.render === 'home') {
    return (
      <>
        <Box sx={{ height: 110, borderRadius: 2.5, mb: 1.5, backgroundImage: `linear-gradient(180deg, rgba(8,17,13,0.08), rgba(8,17,13,0.48)), url(${slide.image})`, backgroundSize: 'cover', backgroundPosition: 'center' }} />
        <Typography sx={{ color: 'rgba(243,236,223,0.58)', fontSize: '0.72rem', letterSpacing: '0.08em', mb: 0.65 }}>{slide.eyebrow}</Typography>
        <Typography sx={{ color: tokens.cream, fontSize: '1rem', fontWeight: 700, mb: 0.35 }}>{slide.title}</Typography>
        <Typography sx={{ color: tokens.textMuted, fontSize: '0.8rem', mb: 1.4, lineHeight: 1.55 }}>{slide.blurb}</Typography>
        <Box sx={{ p: 1.6, borderRadius: 2, border: `1px solid ${tokens.border}`, bgcolor: 'rgba(255,255,255,0.03)', mb: 1.2 }}>
          <Typography sx={{ color: tokens.cream, fontFamily: '"Syne", "Oxygen", sans-serif', fontSize: '2.2rem', lineHeight: 1, fontWeight: 700 }}>16:32</Typography>
          <Typography sx={{ color: tokens.accentStrong, fontSize: '0.82rem', mt: 0.55 }}>16:8 fast active</Typography>
        </Box>
        <Grid container spacing={1}>
          {[
            ['Hydration', '1.6 / 2.5L'],
            ['Steps', '7.8k / 10k'],
            ['Streak', '12 days'],
            ['Exercise', '0.8 / 1h'],
          ].map(([label, value]) => (
            <Grid item xs={6} key={label}>
              <Box sx={{ p: 1.2, borderRadius: 2, border: `1px solid ${tokens.border}`, bgcolor: 'rgba(255,255,255,0.03)' }}>
                <Typography sx={{ color: tokens.textMuted, fontSize: '0.7rem', mb: 0.2 }}>{label}</Typography>
                <Typography sx={{ color: tokens.cream, fontSize: '0.82rem', fontWeight: 600 }}>{value}</Typography>
              </Box>
            </Grid>
          ))}
        </Grid>
      </>
    );
  }

  if (slide.render === 'food') {
    return (
      <>
        <Box sx={{ height: 120, borderRadius: 2.5, mb: 1.5, backgroundImage: `linear-gradient(180deg, rgba(8,17,13,0.08), rgba(8,17,13,0.44)), url(${slide.image})`, backgroundSize: 'cover', backgroundPosition: 'center' }} />
        <Typography sx={{ color: 'rgba(243,236,223,0.58)', fontSize: '0.72rem', letterSpacing: '0.08em', mb: 0.65 }}>{slide.eyebrow}</Typography>
        <Typography sx={{ color: tokens.cream, fontSize: '1rem', fontWeight: 700, mb: 0.35 }}>{slide.title}</Typography>
        <Typography sx={{ color: tokens.textMuted, fontSize: '0.8rem', mb: 1.4, lineHeight: 1.55 }}>{slide.blurb}</Typography>
        <Box sx={{ p: 1.4, borderRadius: 2, border: `1px solid ${tokens.border}`, bgcolor: 'rgba(255,255,255,0.03)', mb: 1.1 }}>
          <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.6 }}>
            <Typography sx={{ color: tokens.textMuted, fontSize: '0.72rem' }}>Calories today</Typography>
            <Typography sx={{ color: tokens.cream, fontSize: '0.72rem' }}>1,480 / 2,000</Typography>
          </Stack>
          <Box sx={{ height: 10, bgcolor: 'rgba(255,255,255,0.07)', borderRadius: 1.5, overflow: 'hidden', mb: 0.75 }}>
            <Box sx={{ width: '74%', height: '100%', bgcolor: tokens.accentStrong }} />
          </Box>
          <Typography sx={{ color: tokens.textSoft, fontSize: '0.76rem' }}>Breakfast, lunch, dinner, and snacks stay grouped.</Typography>
        </Box>
        <Grid container spacing={1}>
          {[
            ['Protein', '91 / 150g'],
            ['Carbs', '132 / 200g'],
            ['Fat', '54 / 67g'],
            ['Recent', '6 foods'],
          ].map(([label, value]) => (
            <Grid item xs={6} key={label}>
              <Box sx={{ p: 1.2, borderRadius: 2, border: `1px solid ${tokens.border}`, bgcolor: 'rgba(255,255,255,0.03)' }}>
                <Typography sx={{ color: tokens.textMuted, fontSize: '0.7rem', mb: 0.2 }}>{label}</Typography>
                <Typography sx={{ color: tokens.cream, fontSize: '0.82rem', fontWeight: 600 }}>{value}</Typography>
              </Box>
            </Grid>
          ))}
        </Grid>
      </>
    );
  }

  if (slide.render === 'progress') {
    return (
      <>
        <Box sx={{ height: 120, borderRadius: 2.5, mb: 1.5, backgroundImage: `linear-gradient(180deg, rgba(8,17,13,0.1), rgba(8,17,13,0.44)), url(${slide.image})`, backgroundSize: 'cover', backgroundPosition: 'center' }} />
        <Typography sx={{ color: 'rgba(243,236,223,0.58)', fontSize: '0.72rem', letterSpacing: '0.08em', mb: 0.65 }}>{slide.eyebrow}</Typography>
        <Typography sx={{ color: tokens.cream, fontSize: '1rem', fontWeight: 700, mb: 0.35 }}>{slide.title}</Typography>
        <Typography sx={{ color: tokens.textMuted, fontSize: '0.8rem', mb: 1.4, lineHeight: 1.55 }}>{slide.blurb}</Typography>
        <Box sx={{ p: 1.4, borderRadius: 2, border: `1px solid ${tokens.border}`, bgcolor: 'rgba(255,255,255,0.03)', mb: 1.1 }}>
          <svg width="100%" height="120" viewBox="0 0 220 120" preserveAspectRatio="none">
            <path d="M0 72 C18 58, 34 84, 56 70 S98 40, 122 60 S164 92, 186 64 S210 34, 220 42" fill="none" stroke="#c5d798" strokeWidth="3" />
            <path d="M0 92 C24 82, 46 98, 70 88 S126 58, 148 70 S188 88, 220 62" fill="none" stroke="rgba(169,191,122,0.44)" strokeWidth="2.2" />
          </svg>
        </Box>
        <Grid container spacing={1}>
          {[
            ['Fasted', '5 / 7 days'],
            ['Weight', '-0.6 kg'],
            ['Steps', '62.4k'],
            ['Calories', '10.2k'],
          ].map(([label, value]) => (
            <Grid item xs={6} key={label}>
              <Box sx={{ p: 1.2, borderRadius: 2, border: `1px solid ${tokens.border}`, bgcolor: 'rgba(255,255,255,0.03)' }}>
                <Typography sx={{ color: tokens.textMuted, fontSize: '0.7rem', mb: 0.2 }}>{label}</Typography>
                <Typography sx={{ color: tokens.cream, fontSize: '0.82rem', fontWeight: 600 }}>{value}</Typography>
              </Box>
            </Grid>
          ))}
        </Grid>
      </>
    );
  }

  if (slide.render === 'protocols') {
    return (
      <>
        <Box sx={{ height: 120, borderRadius: 2.5, mb: 1.5, backgroundImage: `linear-gradient(180deg, rgba(8,17,13,0.08), rgba(8,17,13,0.44)), url(${slide.image})`, backgroundSize: 'cover', backgroundPosition: 'center' }} />
        <Typography sx={{ color: 'rgba(243,236,223,0.58)', fontSize: '0.72rem', letterSpacing: '0.08em', mb: 0.65 }}>{slide.eyebrow}</Typography>
        <Typography sx={{ color: tokens.cream, fontSize: '1rem', fontWeight: 700, mb: 0.35 }}>{slide.title}</Typography>
        <Typography sx={{ color: tokens.textMuted, fontSize: '0.8rem', mb: 1.4, lineHeight: 1.55 }}>{slide.blurb}</Typography>
        <Stack spacing={0.9}>
          {['16:8', '18:6', '20:4', '24h'].map((protocol, index) => (
            <Box key={protocol} sx={{ p: 1.2, borderRadius: 2, border: `1px solid ${tokens.border}`, bgcolor: index === 0 ? 'rgba(169,191,122,0.1)' : 'rgba(255,255,255,0.03)' }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography sx={{ color: tokens.cream, fontWeight: 700 }}>{protocol}</Typography>
                <Typography sx={{ color: tokens.textMuted, fontSize: '0.75rem' }}>{index === 0 ? 'Selected' : 'Available'}</Typography>
              </Stack>
            </Box>
          ))}
        </Stack>
      </>
    );
  }

  if (slide.render === 'settings') {
    return (
      <>
        <Box sx={{ height: 110, borderRadius: 2.5, mb: 1.5, backgroundImage: `linear-gradient(180deg, rgba(8,17,13,0.08), rgba(8,17,13,0.44)), url(${slide.image})`, backgroundSize: 'cover', backgroundPosition: 'center' }} />
        <Typography sx={{ color: 'rgba(243,236,223,0.58)', fontSize: '0.72rem', letterSpacing: '0.08em', mb: 0.65 }}>{slide.eyebrow}</Typography>
        <Typography sx={{ color: tokens.cream, fontSize: '1rem', fontWeight: 700, mb: 0.35 }}>{slide.title}</Typography>
        <Typography sx={{ color: tokens.textMuted, fontSize: '0.8rem', mb: 1.4, lineHeight: 1.55 }}>{slide.blurb}</Typography>
        <Stack spacing={1}>
          {[
            ['Notifications', 'On'],
            ['Fasting reminders', 'On'],
            ['Motivational quotes', 'On'],
            ['Use metric system', 'On'],
          ].map(([label, value]) => (
            <Box key={label} sx={{ px: 1.2, py: 1.05, borderRadius: 2, border: `1px solid ${tokens.border}`, bgcolor: 'rgba(255,255,255,0.03)' }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography sx={{ color: tokens.cream, fontSize: '0.82rem' }}>{label}</Typography>
                <Typography sx={{ color: tokens.textMuted, fontSize: '0.74rem' }}>{value}</Typography>
              </Stack>
            </Box>
          ))}
        </Stack>
      </>
    );
  }

  return (
    <>
      <Box sx={{ height: 110, borderRadius: 2.5, mb: 1.5, backgroundImage: `linear-gradient(180deg, rgba(8,17,13,0.08), rgba(8,17,13,0.44)), url(${slide.image})`, backgroundSize: 'cover', backgroundPosition: 'center' }} />
      <Typography sx={{ color: 'rgba(243,236,223,0.58)', fontSize: '0.72rem', letterSpacing: '0.08em', mb: 0.65 }}>{slide.eyebrow}</Typography>
      <Typography sx={{ color: tokens.cream, fontSize: '1rem', fontWeight: 700, mb: 0.35 }}>{slide.title}</Typography>
      <Typography sx={{ color: tokens.textMuted, fontSize: '0.8rem', mb: 1.4, lineHeight: 1.55 }}>{slide.blurb}</Typography>
      <Box sx={{ p: 1.4, borderRadius: 2, border: `1px solid ${tokens.border}`, bgcolor: 'rgba(255,255,255,0.03)', mb: 1 }}>
        <Typography sx={{ color: tokens.cream, fontWeight: 700, mb: 0.35 }}>FastTime Premium</Typography>
        <Typography sx={{ color: tokens.textSoft, fontSize: '0.78rem' }}>Active plan · Renewal on Apr 28</Typography>
      </Box>
      <Stack spacing={1}>
        {[
          ['Store billing', 'Managed in App Store / Google Play'],
          ['Trial status', 'Available'],
          ['Plan sync', 'Automatic'],
        ].map(([label, value]) => (
          <Box key={label} sx={{ px: 1.2, py: 1.05, borderRadius: 2, border: `1px solid ${tokens.border}`, bgcolor: 'rgba(255,255,255,0.03)' }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Typography sx={{ color: tokens.cream, fontSize: '0.82rem' }}>{label}</Typography>
              <Typography sx={{ color: tokens.textMuted, fontSize: '0.74rem' }}>{value}</Typography>
            </Stack>
          </Box>
        ))}
      </Stack>
    </>
  );
}

export default function WelcomePage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [waitlistEmail, setWaitlistEmail] = useState('');
  const [waitlistSubmitting, setWaitlistSubmitting] = useState(false);
  const [waitlistFeedback, setWaitlistFeedback] = useState(null);
  const [activeScreenshot, setActiveScreenshot] = useState(0);
  const prefersReducedMotion = useReducedMotion();
  const { scrollYProgress } = useScroll();
  const heroY = useTransform(scrollYProgress, [0, 1], prefersReducedMotion ? [0, 0] : [0, -120]);
  const heroImageY = useTransform(scrollYProgress, [0, 1], prefersReducedMotion ? [0, 0] : [0, -90]);
  const glowY = useTransform(scrollYProgress, [0, 1], prefersReducedMotion ? [0, 0] : [0, -180]);
  const dashboardY = useTransform(scrollYProgress, [0, 1], prefersReducedMotion ? [0, 0] : [0, -40]);

  useEffect(() => {
    if (prefersReducedMotion) {
      return undefined;
    }

    const timer = window.setInterval(() => {
      setActiveScreenshot((current) => (current + 1) % screenshotSlides.length);
    }, 3800);

    return () => window.clearInterval(timer);
  }, [prefersReducedMotion]);

  const handleWaitlistJoin = async (placement) => {
    const normalizedEmail = waitlistEmail.trim();

    if (!normalizedEmail || !/\S+@\S+\.\S+/.test(normalizedEmail)) {
      setWaitlistFeedback({
        severity: 'error',
        message: 'Enter a valid email address to request mobile beta access.',
      });
      return;
    }

    setWaitlistSubmitting(true);
    setWaitlistFeedback(null);

    try {
      const response = await waitlistAPI.join({
        email: normalizedEmail,
        placement,
        source: 'landing-page',
      });

      setWaitlistFeedback({
        severity: 'success',
        message: response.data?.message || 'You are on the list for mobile beta access.',
      });
      setWaitlistEmail('');
    } catch (error) {
      setWaitlistFeedback({
        severity: 'error',
        message: error.response?.data?.message || 'We could not save your email right now. Please try again.',
      });
    } finally {
      setWaitlistSubmitting(false);
    }
  };

  const previousScreenshot = (activeScreenshot - 1 + screenshotSlides.length) % screenshotSlides.length;
  const nextScreenshot = (activeScreenshot + 1) % screenshotSlides.length;

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: tokens.bg, color: tokens.text }}>
      <AppBar position="sticky" elevation={0}>
        <Container maxWidth="xl" sx={{ px: { xs: 2, md: 4 } }}>
          <Toolbar sx={{ minHeight: 84, px: '0 !important' }}>
            <BrandLogo />
            <Stack
              direction="row"
              spacing={4}
              sx={{ ml: 'auto', mr: 4, display: { xs: 'none', md: 'flex' }, color: tokens.cream }}
            >
              <Button color="inherit" onClick={() => document.getElementById('story')?.scrollIntoView({ behavior: 'smooth' })}>
                Story
              </Button>
              <Button color="inherit" onClick={() => document.getElementById('flow')?.scrollIntoView({ behavior: 'smooth' })}>
                Flow
              </Button>
              <Button color="inherit" onClick={() => document.getElementById('cta')?.scrollIntoView({ behavior: 'smooth' })}>
                Start
              </Button>
            </Stack>
            <Stack direction="row" spacing={1.5}>
              {!user && <Button color="inherit" onClick={() => navigate('/login')}>Log in</Button>}
              <Button
                variant="contained"
                onClick={() => navigate(user ? '/dashboard' : '/signup')}
                sx={{ bgcolor: '#8a9275', color: '#fff8eb', px: 3.5, '&:hover': { bgcolor: '#7c8568' } }}
              >
                Get started
              </Button>
            </Stack>
          </Toolbar>
        </Container>
      </AppBar>

      <Box
        sx={{
          position: 'relative',
          overflow: 'hidden',
          borderBottom: `1px solid ${tokens.border}`,
          background:
            'radial-gradient(circle at 12% 18%, rgba(169,191,122,0.18), transparent 24%), radial-gradient(circle at 88% 12%, rgba(201,139,103,0.14), transparent 18%), linear-gradient(180deg, #09120e 0%, #08110d 100%)',
        }}
      >
        <MotionBox
          style={{ y: glowY }}
          sx={{
            position: 'absolute',
            inset: '-10% auto auto -12%',
            width: 420,
            height: 420,
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(169,191,122,0.2) 0%, rgba(169,191,122,0) 72%)',
            pointerEvents: 'none',
          }}
        />
        <MotionBox
          style={{ y: heroImageY }}
          sx={{
            position: 'absolute',
            inset: { xs: 'auto -35% -5% auto', md: '0 -10% auto auto' },
            width: { xs: 320, md: 640 },
            height: { xs: 380, md: 760 },
            backgroundImage: `linear-gradient(180deg, rgba(8,17,13,0.12), rgba(8,17,13,0.46)), url(${landingForest})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            opacity: 0.36,
            filter: 'saturate(0.9)',
            maskImage: 'linear-gradient(180deg, rgba(0,0,0,0.9), rgba(0,0,0,0.2))',
            pointerEvents: 'none',
          }}
        />

        <Container maxWidth="xl" sx={{ px: { xs: 2, md: 4 }, py: { xs: 8, md: 12 } }}>
          <Grid container spacing={{ xs: 5, md: 8 }} alignItems="center">
            <Grid item xs={12} lg={6}>
              <MotionBox style={{ y: heroY }}>
                <MotionBox
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.7 }}
                  sx={{
                    mb: 2.8,
                    maxWidth: 560,
                    p: { xs: 1.2, sm: 1.35 },
                    borderRadius: 2.5,
                    border: `1px solid rgba(188, 204, 166, 0.18)`,
                    background: 'rgba(10,18,14,0.78)',
                    backdropFilter: 'blur(18px)',
                  }}
                >
                  {waitlistFeedback ? (
                    <Alert
                      severity={waitlistFeedback.severity}
                      sx={{
                        mb: 1,
                        bgcolor: waitlistFeedback.severity === 'success' ? 'rgba(146,168,107,0.14)' : 'rgba(230,124,115,0.12)',
                        color: tokens.cream,
                        '& .MuiAlert-icon': { color: 'inherit' },
                      }}
                    >
                      {waitlistFeedback.message}
                    </Alert>
                  ) : null}
                  <Stack spacing={1.2}>
                    <Stack
                      direction={{ xs: 'column', sm: 'row' }}
                      spacing={1.2}
                      alignItems={{ xs: 'flex-start', sm: 'center' }}
                      sx={{ px: 0.2 }}
                    >
                      <Box
                        component="img"
                        src={fastTimeAppIcon}
                        alt="FastTime app icon"
                        sx={{
                          width: 48,
                          height: 48,
                          borderRadius: 1.4,
                          objectFit: 'cover',
                          boxShadow: '0 12px 28px rgba(0,0,0,0.22)',
                          flexShrink: 0,
                        }}
                      />
                      <Box>
                        <Typography sx={{ color: tokens.accentStrong, fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.12em', mb: 0.35 }}>
                          MOBILE BETA
                        </Typography>
                        <Typography sx={{ color: tokens.cream, fontSize: { xs: '1rem', sm: '1.08rem' }, fontWeight: 700, mb: 0.25 }}>
                          Want early access to the iPhone app?
                        </Typography>
                        <Typography sx={{ color: 'rgba(243,236,223,0.7)', fontSize: '0.84rem', lineHeight: 1.55, maxWidth: 430 }}>
                          FastTime on the web is live now. Leave your email if you want an invite to test the iPhone beta before the public launch.
                        </Typography>
                      </Box>
                    </Stack>

                    <Stack
                      direction={{ xs: 'column', sm: 'row' }}
                      spacing={0.9}
                      alignItems="stretch"
                    >
                      <TextField
                        placeholder="Email address"
                        type="email"
                        value={waitlistEmail}
                        onChange={(event) => setWaitlistEmail(event.target.value)}
                        sx={{
                          flex: 1,
                          minWidth: { sm: 280 },
                          '& .MuiInputBase-root': {
                            height: '100%',
                            bgcolor: 'rgba(255,255,255,0.045)',
                            borderRadius: 1.6,
                          },
                          '& .MuiOutlinedInput-input': {
                            py: 1.35,
                            fontSize: '0.95rem',
                          },
                        }}
                      />
                      <Button
                        variant="contained"
                        onClick={() => handleWaitlistJoin('hero')}
                        disabled={waitlistSubmitting}
                        sx={{
                          whiteSpace: 'nowrap',
                          minHeight: { sm: 56 },
                          px: 2.4,
                          borderRadius: 1.6,
                          background: 'linear-gradient(135deg, #d7f6ab 0%, #90eb70 52%, #59cb69 100%)',
                          color: '#08110d',
                          boxShadow: '0 14px 26px rgba(105, 220, 107, 0.18)',
                          '&:hover': {
                            background: 'linear-gradient(135deg, #e2f9c0 0%, #9bf07a 52%, #64d473 100%)',
                          },
                        }}
                      >
                        {waitlistSubmitting ? 'Saving...' : 'Request beta access'}
                      </Button>
                    </Stack>
                  </Stack>
                </MotionBox>

                <MotionTypography
                  variant="h1"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.7 }}
                  sx={{
                    color: tokens.cream,
                    maxWidth: 700,
                    fontSize: { xs: '2.65rem', sm: '3.55rem', lg: '4.35rem' },
                    lineHeight: 1,
                    letterSpacing: '-0.022em',
                    mb: 3,
                  }}
                >
                  Fasting,
                  <br />
                  food logging,
                  <br />
                  and progress.
                </MotionTypography>

                <MotionTypography
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.7, delay: 0.08 }}
                  sx={{
                    color: 'rgba(243,236,223,0.82)',
                    fontSize: { xs: '0.98rem', md: '1.08rem' },
                    lineHeight: 1.72,
                    maxWidth: 520,
                    mb: 3.5,
                  }}
                >
                  A calmer way to track your fasts, meals, habits, and weekly momentum — all in one place, starting on the web today.
                </MotionTypography>

                <MotionBox
                  initial={{ opacity: 0, y: 18 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.7, delay: 0.16 }}
                >
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 4.5, maxWidth: 520 }}>
                    <Button
                      variant="contained"
                      size="large"
                      onClick={() => navigate(user ? '/dashboard' : '/signup')}
                      sx={{ bgcolor: tokens.accent, color: tokens.bg, py: 1.9, px: 3.4 }}
                    >
                      Get started for free
                    </Button>
                    <Button
                      variant="outlined"
                      size="large"
                      onClick={() => document.getElementById('story')?.scrollIntoView({ behavior: 'smooth' })}
                      sx={{ py: 1.9, px: 3.4 }}
                    >
                      See the flow
                    </Button>
                  </Stack>
                  <Typography sx={{ color: tokens.textMuted, fontSize: '0.84rem', maxWidth: 520 }}>
                    Prefer mobile? You can create your account now on web and request access to the iPhone beta below.
                  </Typography>
                </MotionBox>

                <Grid container spacing={1.5} sx={{ maxWidth: 620 }}>
                  {heroHighlights.map((item, index) => (
                    <Grid item xs={12} sm={4} key={item.label}>
                      <MotionBox
                        initial={{ opacity: 0, y: 12 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.55, delay: 0.22 + index * 0.08 }}
                        sx={{
                          p: 2.2,
                          borderRadius: 2,
                          border: `1px solid ${tokens.border}`,
                          bgcolor: 'rgba(255,255,255,0.03)',
                        }}
                      >
                        <Typography sx={{ color: tokens.cream, fontSize: '1.25rem', fontWeight: 600, mb: 0.4 }}>
                          {item.value}
                        </Typography>
                        <Typography sx={{ color: 'rgba(243,236,223,0.68)', fontSize: '0.92rem', lineHeight: 1.55 }}>
                          {item.label}
                        </Typography>
                      </MotionBox>
                    </Grid>
                  ))}
                </Grid>
              </MotionBox>
            </Grid>

            <Grid item xs={12} lg={6}>
              <MotionBox
                style={{ y: dashboardY }}
                animate={prefersReducedMotion ? {} : { y: [0, -10, 0] }}
                transition={prefersReducedMotion ? {} : { duration: 7.5, repeat: Infinity, ease: 'easeInOut' }}
              >
                <GlassPanel sx={{ p: { xs: 2.2, md: 2.8 } }}>
                  <Stack spacing={2.2}>
                    <Stack direction="row" alignItems="center" justifyContent="space-between">
                      <Stack spacing={0.35}>
                        <Typography sx={{ color: tokens.cream, fontSize: '1rem', fontWeight: 600 }}>
                          Your day, in one view
                        </Typography>
                        <Typography sx={{ color: 'rgba(243,236,223,0.68)', fontSize: '0.92rem' }}>
                          Fast, meals, goals, and progress — all easy to scan.
                        </Typography>
                      </Stack>
                      <Stack direction="row" spacing={1.1} alignItems="center">
                        <NotificationsNoneOutlined sx={{ color: tokens.textSoft, fontSize: 21 }} />
                        <Avatar sx={{ width: 34, height: 34, bgcolor: 'rgba(255,255,255,0.08)' }}>A</Avatar>
                      </Stack>
                    </Stack>

                    <Grid container spacing={2}>
                      <Grid item xs={12} md={6.6}>
                        <Box
                          sx={{
                            p: { xs: 2.5, md: 3 },
                            borderRadius: 2.5,
                            minHeight: 340,
                            border: `1px solid ${tokens.border}`,
                            background:
                              'radial-gradient(circle at top, rgba(169,191,122,0.16), transparent 44%), rgba(255,255,255,0.025)',
                          }}
                        >
                          <Typography sx={{ color: 'rgba(243,236,223,0.72)', fontSize: '0.86rem', letterSpacing: '0.08em', mb: 1 }}>
                            CURRENT FAST
                          </Typography>
                          <Typography
                            sx={{
                              color: tokens.cream,
                              fontFamily: '"Syne", "Oxygen", sans-serif',
                              fontSize: { xs: '3rem', md: '3.6rem' },
                              lineHeight: 1,
                              mb: 1.3,
                              fontWeight: 700,
                              letterSpacing: '-0.02em',
                            }}
                          >
                            16:32:47
                          </Typography>
                          <Typography sx={{ color: tokens.accentStrong, mb: 3 }}>16:8 fast · active now</Typography>

                          <Box
                            sx={{
                              height: 12,
                              borderRadius: 2,
                              overflow: 'hidden',
                              bgcolor: 'rgba(255,255,255,0.08)',
                              mb: 2.5,
                            }}
                          >
                            <MotionBox
                              initial={{ width: 0 }}
                              whileInView={{ width: '86%' }}
                              viewport={{ once: true }}
                              transition={{ duration: 1.1, ease: 'easeOut' }}
                              sx={{
                                height: '100%',
                                borderRadius: 2,
                                background: 'linear-gradient(90deg, #92a86b 0%, #c5d798 100%)',
                              }}
                            />
                          </Box>

                          <Stack direction="row" spacing={1.2} flexWrap="wrap" useFlexGap>
                            {['Started yesterday 8:00 PM', 'Ends today 12:00 PM', '12 day streak'].map((item) => (
                              <Box
                                key={item}
                                sx={{
                                  px: 1.4,
                                  py: 1,
                                  borderRadius: 1.5,
                                  bgcolor: 'rgba(255,255,255,0.045)',
                                  border: `1px solid ${tokens.border}`,
                                }}
                              >
                                <Typography sx={{ color: tokens.textSoft, fontSize: '0.82rem' }}>{item}</Typography>
                              </Box>
                            ))}
                          </Stack>
                        </Box>
                      </Grid>

                      <Grid item xs={12} md={5.4}>
                        <Stack spacing={1.4} sx={{ height: '100%' }}>
                          {dashboardTiles.map((tile, index) => (
                            <MotionBox
                              key={tile.title}
                              initial={{ opacity: 0, x: 20 }}
                              whileInView={{ opacity: 1, x: 0 }}
                              viewport={{ once: true }}
                              transition={{ duration: 0.5, delay: index * 0.08 }}
                              sx={{
                                p: 2,
                                borderRadius: 2,
                                border: `1px solid ${tokens.border}`,
                                bgcolor: 'rgba(255,255,255,0.03)',
                                flex: 1,
                              }}
                            >
                              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.2 }}>
                                {tile.icon}
                                <MonitorHeartOutlined sx={{ color: 'rgba(243,236,223,0.4)', fontSize: 18 }} />
                              </Stack>
                              <Typography sx={{ color: 'rgba(243,236,223,0.7)', fontSize: '0.84rem', mb: 0.35 }}>
                                {tile.title}
                              </Typography>
                              <Typography sx={{ color: tokens.cream, fontSize: '1.24rem', fontWeight: 600, mb: 0.25 }}>
                                {tile.value}
                              </Typography>
                              <Typography sx={{ color: tokens.textMuted, fontSize: '0.84rem' }}>
                                {tile.detail}
                              </Typography>
                            </MotionBox>
                          ))}
                        </Stack>
                      </Grid>
                    </Grid>
                  </Stack>
                </GlassPanel>
              </MotionBox>
            </Grid>
          </Grid>

          <FadeSection sx={{ mt: { xs: 7, md: 9 } }}>
            <MotionBox
              animate={prefersReducedMotion ? {} : { y: [0, 8, 0] }}
              transition={prefersReducedMotion ? {} : { duration: 2.2, repeat: Infinity, ease: 'easeInOut' }}
            >
              <Stack direction="row" spacing={1} alignItems="center" justifyContent="center" sx={{ color: tokens.textMuted }}>
                <Typography sx={{ fontSize: '0.9rem' }}>Scroll for the calmer breakdown</Typography>
                <KeyboardArrowDownRounded />
              </Stack>
            </MotionBox>
          </FadeSection>
        </Container>
      </Box>

      <Container id="story" maxWidth="xl" sx={{ px: { xs: 2, md: 4 }, py: { xs: 8, md: 12 } }}>
        <FadeSection>
          <Grid container spacing={{ xs: 2.5, md: 4 }} alignItems="end" sx={{ mb: { xs: 4.5, md: 6.5 } }}>
            <Grid item xs={12} md={7}>
              <Typography variant="h2" sx={{ color: tokens.cream, fontSize: { xs: '2.6rem', md: '4rem' }, mb: 1.5 }}>
                Built for consistency.
              </Typography>
              <Typography sx={{ color: tokens.textSoft, fontSize: { xs: '1rem', md: '1.08rem' }, maxWidth: 620 }}>
                Plan your fasts, log your day, and keep your progress in view with less noise.
              </Typography>
            </Grid>
            <Grid item xs={12} md={5}>
              <Grid container spacing={1.5}>
                  {[
                  { label: 'Daily tracking', value: 'Fasts, meals, habits' },
                  { label: 'Account tools', value: 'Settings, billing, security' },
                ].map((item) => (
                  <Grid item xs={12} sm={6} key={item.label}>
                    <Box sx={{ p: 2.2, borderRadius: 2, border: `1px solid ${tokens.border}`, bgcolor: 'rgba(255,255,255,0.02)' }}>
                      <Typography sx={{ color: tokens.cream, fontSize: '1rem', fontWeight: 600, mb: 0.45 }}>{item.value}</Typography>
                      <Typography sx={{ color: tokens.textMuted, fontSize: '0.88rem' }}>{item.label}</Typography>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </Grid>
          </Grid>
        </FadeSection>

        <Stack spacing={3}>
          {featureStories.map((story, index) => (
            <FadeSection key={story.title}>
              <Grid
                container
                spacing={{ xs: 2.5, md: 4 }}
                alignItems="center"
                direction={index % 2 === 1 ? 'row-reverse' : 'row'}
              >
                <Grid item xs={12} md={6}>
                  <Box
                    sx={{
                      p: { xs: 3, md: 4 },
                      borderRadius: 3,
                      border: `1px solid ${tokens.border}`,
                      bgcolor: story.accent,
                    }}
                  >
                    <Typography variant="h3" sx={{ color: tokens.cream, fontSize: { xs: '2.15rem', md: '3rem' }, mb: 1.5 }}>
                      {story.title}
                    </Typography>
                    <Typography sx={{ color: tokens.textSoft, fontSize: '1rem', lineHeight: 1.8, mb: 2.2, maxWidth: 520 }}>
                      {story.copy}
                    </Typography>
                    <Stack direction="row" spacing={1.1} flexWrap="wrap" useFlexGap>
                      {story.stats.map((item) => (
                        <Box
                          key={item}
                          sx={{
                            px: 1.4,
                            py: 1,
                            borderRadius: 1.5,
                            border: `1px solid ${tokens.border}`,
                            bgcolor: 'rgba(8,17,13,0.24)',
                          }}
                        >
                          <Typography sx={{ color: tokens.cream, fontSize: '0.84rem' }}>{item}</Typography>
                        </Box>
                      ))}
                    </Stack>
                  </Box>
                </Grid>

                <Grid item xs={12} md={6}>
                  <MotionBox
                    whileInView={{ y: prefersReducedMotion ? 0 : [26, 0], opacity: [0.86, 1] }}
                    viewport={{ once: true, amount: 0.35 }}
                    transition={{ duration: 0.95, ease: [0.22, 1, 0.36, 1] }}
                    sx={{
                      height: { xs: 280, md: 360 },
                      borderRadius: 3,
                      border: `1px solid ${tokens.border}`,
                      overflow: 'hidden',
                      position: 'relative',
                      backgroundImage: `linear-gradient(180deg, rgba(8,17,13,0.12), rgba(8,17,13,0.5)), url(${story.image})`,
                      backgroundSize: 'cover',
                      backgroundPosition: 'center',
                    }}
                    >
                      <MotionBox
                      animate={prefersReducedMotion ? {} : { y: [-10, 10, -10] }}
                      transition={prefersReducedMotion ? {} : { duration: 7, repeat: Infinity, ease: 'easeInOut' }}
                      sx={{
                        position: 'absolute',
                        right: { xs: 18, md: 28 },
                        bottom: { xs: 18, md: 28 },
                        width: { xs: 'calc(100% - 36px)', md: 300 },
                        p: 2.2,
                        borderRadius: 2,
                        bgcolor: 'rgba(7,16,13,0.72)',
                        backdropFilter: 'blur(12px)',
                        border: `1px solid ${tokens.border}`,
                      }}
                    >
                      <Typography sx={{ color: 'rgba(243,236,223,0.72)', fontSize: '0.82rem', letterSpacing: '0.08em', mb: 0.8 }}>
                        DESIGNED FOR DAILY RHYTHM
                      </Typography>
                      <Typography sx={{ color: tokens.cream, fontSize: '1.2rem', fontWeight: 600, mb: 0.45 }}>
                        Built to feel simple when your day is not.
                      </Typography>
                      <Typography sx={{ color: tokens.textSoft, fontSize: '0.9rem', lineHeight: 1.6 }}>
                        Move from starting a fast to checking your week without friction.
                      </Typography>
                    </MotionBox>
                  </MotionBox>
                </Grid>
              </Grid>
            </FadeSection>
          ))}
        </Stack>
      </Container>

      <Box id="flow" sx={{ borderTop: `1px solid ${tokens.border}`, borderBottom: `1px solid ${tokens.border}`, bgcolor: 'rgba(255,255,255,0.02)' }}>
        <Container maxWidth="xl" sx={{ px: { xs: 2, md: 4 }, py: { xs: 8, md: 10 } }}>
          <FadeSection>
            <Typography variant="h2" sx={{ color: tokens.cream, fontSize: { xs: '2.5rem', md: '3.7rem' }, maxWidth: 680, mb: 1.6 }}>
              From fast to finish, the flow stays clear.
            </Typography>
            <Typography sx={{ color: tokens.textSoft, maxWidth: 620, mb: { xs: 4, md: 5.5 } }}>
              Start a fast, log the day, and check your progress — with motion that supports the experience, not distracts from it.
            </Typography>
          </FadeSection>

          <Grid container spacing={2}>
            {dailyFlow.map((item, index) => (
              <Grid item xs={12} md={4} key={item.title}>
                <FadeSection>
                  <Box
                    sx={{
                      p: 3,
                      height: '100%',
                      borderRadius: 2.5,
                      border: `1px solid ${tokens.border}`,
                      bgcolor: 'rgba(255,255,255,0.025)',
                    }}
                  >
                    <Stack direction="row" spacing={1.3} alignItems="center" sx={{ mb: 2 }}>
                      <Box
                        sx={{
                          width: 52,
                          height: 52,
                          borderRadius: '50%',
                          display: 'grid',
                          placeItems: 'center',
                          bgcolor: 'rgba(169,191,122,0.12)',
                          border: `1px solid ${tokens.border}`,
                        }}
                      >
                        {item.icon}
                      </Box>
                      <Typography sx={{ color: tokens.textMuted, fontSize: '0.9rem' }}>0{index + 1}</Typography>
                    </Stack>
                    <Typography sx={{ color: tokens.cream, fontSize: '1.4rem', fontWeight: 600, mb: 1 }}>
                      {item.title}
                    </Typography>
                    <Typography sx={{ color: tokens.textSoft, lineHeight: 1.8 }}>
                      {item.copy}
                    </Typography>
                  </Box>
                </FadeSection>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      <Container maxWidth="xl" sx={{ px: { xs: 2, md: 4 }, py: { xs: 8, md: 11 } }}>
        <FadeSection>
          <Grid container spacing={{ xs: 3, md: 4 }} alignItems="end" sx={{ mb: { xs: 4, md: 6 } }}>
            <Grid item xs={12} md={7}>
              <Typography variant="h2" sx={{ color: tokens.cream, fontSize: { xs: '2.5rem', md: '3.8rem' }, mb: 1.4 }}>
                See the app before you dive in.
              </Typography>
              <Typography sx={{ color: tokens.textSoft, maxWidth: 640, lineHeight: 1.8 }}>
                A closer look at the experience: your home screen, your food log, and the tools that keep everything running smoothly.
              </Typography>
            </Grid>
            <Grid item xs={12} md={5}>
              <Box sx={{ p: 2.2, borderRadius: 2, border: `1px solid ${tokens.border}`, bgcolor: 'rgba(255,255,255,0.02)' }}>
                <Typography sx={{ color: tokens.cream, fontSize: '1rem', fontWeight: 600, mb: 0.45 }}>
                  App screenshots
                </Typography>
                <Typography sx={{ color: tokens.textMuted, fontSize: '0.9rem', lineHeight: 1.7 }}>
                  Preview the product before you get started.
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </FadeSection>

        <FadeSection>
          <Box
            sx={{
              position: 'relative',
              minHeight: { xs: 540, md: 720 },
              display: 'grid',
              placeItems: 'center',
              overflow: 'hidden',
            }}
          >
            <MotionBox
              animate={prefersReducedMotion ? {} : { opacity: [0.45, 0.7, 0.45], scale: [0.98, 1.04, 0.98] }}
              transition={prefersReducedMotion ? {} : { duration: 7.5, repeat: Infinity, ease: 'easeInOut' }}
              sx={{
                position: 'absolute',
                inset: '12% 18%',
                borderRadius: '50%',
                background: 'radial-gradient(circle, rgba(169,191,122,0.12) 0%, rgba(0,0,0,0) 72%)',
                filter: 'blur(18px)',
                pointerEvents: 'none',
              }}
            />

            <Box sx={{ position: 'relative', width: '100%', maxWidth: 1180, mx: 'auto' }}>
              <MotionBox
                animate={prefersReducedMotion ? {} : { x: [-4, 4, -4] }}
                transition={prefersReducedMotion ? {} : { duration: 6.5, repeat: Infinity, ease: 'easeInOut' }}
                sx={{
                  position: { xs: 'relative', md: 'absolute' },
                  left: { md: 30 },
                  top: { md: 110 },
                  zIndex: 1,
                  opacity: { xs: 0, md: 0.45 },
                  filter: { md: 'blur(1px) saturate(0.88)' },
                  transform: { md: 'scale(0.86)' },
                  pointerEvents: 'none',
                  display: { xs: 'none', md: 'block' },
                }}
              >
                <PhoneMockup tilt={-7} accent={screenshotSlides[previousScreenshot].accent}>
                  <ScreenshotSlide slide={screenshotSlides[previousScreenshot]} />
                </PhoneMockup>
              </MotionBox>

              <MotionBox
                key={screenshotSlides[activeScreenshot].id}
                initial={{ opacity: 0, y: 24, scale: 0.96 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                transition={{ duration: 0.65, ease: [0.22, 1, 0.36, 1] }}
                sx={{
                  position: 'relative',
                  zIndex: 3,
                  display: 'grid',
                  placeItems: 'center',
                }}
              >
                <PhoneMockup tilt={0} accent={screenshotSlides[activeScreenshot].accent}>
                  <ScreenshotSlide slide={screenshotSlides[activeScreenshot]} />
                </PhoneMockup>
              </MotionBox>

              <MotionBox
                animate={prefersReducedMotion ? {} : { x: [4, -4, 4] }}
                transition={prefersReducedMotion ? {} : { duration: 6.5, repeat: Infinity, ease: 'easeInOut' }}
                sx={{
                  position: { xs: 'relative', md: 'absolute' },
                  right: { md: 30 },
                  top: { md: 110 },
                  zIndex: 1,
                  opacity: { xs: 0, md: 0.45 },
                  filter: { md: 'blur(1px) saturate(0.88)' },
                  transform: { md: 'scale(0.86)' },
                  pointerEvents: 'none',
                  display: { xs: 'none', md: 'block' },
                }}
              >
                <PhoneMockup tilt={7} accent={screenshotSlides[nextScreenshot].accent}>
                  <ScreenshotSlide slide={screenshotSlides[nextScreenshot]} />
                </PhoneMockup>
              </MotionBox>
            </Box>

            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              sx={{
                position: 'absolute',
                top: { xs: 14, md: 26 },
                right: { xs: 0, md: 20 },
                zIndex: 4,
              }}
            >
              <Button
                variant="outlined"
                onClick={() => setActiveScreenshot(previousScreenshot)}
                sx={{ minWidth: 0, width: 42, height: 42, p: 0, borderRadius: '50%' }}
              >
                <KeyboardArrowLeftRounded />
              </Button>
              <Button
                variant="outlined"
                onClick={() => setActiveScreenshot(nextScreenshot)}
                sx={{ minWidth: 0, width: 42, height: 42, p: 0, borderRadius: '50%' }}
              >
                <KeyboardArrowRightRounded />
              </Button>
            </Stack>
          </Box>

          <Grid container spacing={1.2} sx={{ mt: { xs: 3, md: 4 } }}>
            {screenshotSlides.map((slide, index) => (
              <Grid item xs={6} sm={4} md={2} key={slide.id}>
                <MotionBox
                  whileHover={{ y: -4 }}
                  onClick={() => setActiveScreenshot(index)}
                  sx={{
                    p: 1.5,
                    borderRadius: 2.5,
                    cursor: 'pointer',
                    border: `1px solid ${index === activeScreenshot ? 'rgba(169,191,122,0.34)' : tokens.border}`,
                    bgcolor: index === activeScreenshot ? 'rgba(169,191,122,0.08)' : 'rgba(255,255,255,0.02)',
                    transition: 'border-color 180ms ease, background-color 180ms ease',
                  }}
                >
                  <Typography sx={{ color: tokens.cream, fontSize: '0.94rem', fontWeight: 700, mb: 0.25 }}>{slide.title}</Typography>
                  <Typography sx={{ color: tokens.textMuted, fontSize: '0.76rem', lineHeight: 1.45 }}>
                    {slide.blurb}
                  </Typography>
                </MotionBox>
              </Grid>
            ))}
          </Grid>
        </FadeSection>
      </Container>

      <Container maxWidth="xl" sx={{ px: { xs: 2, md: 4 }, py: { xs: 8, md: 11 } }}>
        <FadeSection>
          <Grid container spacing={{ xs: 2.5, md: 4 }} alignItems="center">
            <Grid item xs={12} md={5}>
              <Typography variant="h2" sx={{ color: tokens.cream, fontSize: { xs: '2.5rem', md: '3.8rem' }, mb: 1.6 }}>
                More depth,
                <br />
                less clutter.
              </Typography>
              <Typography sx={{ color: tokens.textSoft, lineHeight: 1.8, maxWidth: 510, mb: 3 }}>
                FastTime gives you the essentials: guided fasting, daily tracking, weekly trends, and account tools that stay out of the way.
              </Typography>
              <Stack direction="row" spacing={1.2} flexWrap="wrap" useFlexGap>
                {[
                  { icon: <FavoriteBorder sx={{ fontSize: 18 }} />, label: '4 fasting protocols' },
                  { icon: <CalendarMonthOutlined sx={{ fontSize: 18 }} />, label: 'Weekly summaries + charts' },
                  { icon: <SpaOutlined sx={{ fontSize: 18 }} />, label: 'Food, macros, settings' },
                ].map((item) => (
                  <Box
                    key={item.label}
                    sx={{
                      px: 1.5,
                      py: 1,
                      borderRadius: 1.5,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.8,
                      border: `1px solid ${tokens.border}`,
                      bgcolor: 'rgba(255,255,255,0.02)',
                      color: tokens.cream,
                    }}
                  >
                    {item.icon}
                    <Typography sx={{ fontSize: '0.9rem' }}>{item.label}</Typography>
                  </Box>
                ))}
              </Stack>
            </Grid>

            <Grid item xs={12} md={7}>
              <GlassPanel
                sx={{
                  p: 2.2,
                  backgroundImage: `linear-gradient(180deg, rgba(7,16,13,0.4), rgba(7,16,13,0.85)), url(${ctaBackground})`,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                }}
              >
                <Grid container spacing={1.6}>
                  {[
                    ['Today', 'An active fast with a clear finish line'],
                    ['Nutrition', 'Meals, calories, and macros in one place'],
                    ['History', 'Weekly trends for steps, calories, and weight'],
                    ['Account', 'Settings and billing that stay in sync'],
                  ].map(([label, value], index) => (
                    <Grid item xs={12} sm={6} key={label}>
                      <MotionBox
                        initial={{ opacity: 0, y: 16 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.55, delay: index * 0.08 }}
                        sx={{
                          p: 2,
                          borderRadius: 2,
                          bgcolor: 'rgba(7,16,13,0.62)',
                          border: `1px solid ${tokens.border}`,
                        }}
                      >
                        <Typography sx={{ color: 'rgba(243,236,223,0.64)', fontSize: '0.82rem', letterSpacing: '0.08em', mb: 0.6 }}>
                          {label}
                        </Typography>
                        <Typography sx={{ color: tokens.cream, fontSize: '1.1rem', fontWeight: 600 }}>
                          {value}
                        </Typography>
                      </MotionBox>
                    </Grid>
                  ))}
                </Grid>
              </GlassPanel>
            </Grid>
          </Grid>
        </FadeSection>
      </Container>

      <Box sx={{ borderTop: `1px solid ${tokens.border}`, borderBottom: `1px solid ${tokens.border}`, bgcolor: 'rgba(255,255,255,0.02)' }}>
        <Container maxWidth="lg" sx={{ px: { xs: 2, md: 4 }, py: { xs: 8, md: 10 } }}>
          <FadeSection>
            <Grid container spacing={{ xs: 3, md: 4 }} alignItems="center">
              <Grid item xs={12} md={6.4}>
                <Typography variant="h2" sx={{ color: tokens.cream, fontSize: { xs: '2.45rem', md: '3.7rem' }, mb: 1.4 }}>
                  Try the mobile beta.
                </Typography>
                <Typography sx={{ color: tokens.textSoft, lineHeight: 1.8, maxWidth: 560, mb: 3 }}>
                  The full web app is ready now. If you want to help test the iPhone app in beta, request access here and we will reach out as spots open.
                </Typography>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.4} sx={{ maxWidth: 560 }}>
                  <TextField
                    fullWidth
                    placeholder="Enter your email"
                    type="email"
                    value={waitlistEmail}
                    onChange={(event) => setWaitlistEmail(event.target.value)}
                    sx={{
                      '& .MuiInputBase-root': {
                        bgcolor: 'rgba(255,255,255,0.03)',
                      },
                    }}
                  />
                  <Button
                    variant="contained"
                    size="large"
                    onClick={() => handleWaitlistJoin('footer')}
                    disabled={waitlistSubmitting}
                    sx={{ minWidth: { sm: 190 }, px: 3.2, bgcolor: tokens.accent, color: tokens.bg }}
                  >
                    {waitlistSubmitting ? 'Saving...' : 'Request beta access'}
                  </Button>
                </Stack>
                <Typography sx={{ color: tokens.textMuted, fontSize: '0.82rem', mt: 1.2 }}>
                  We will use this only for beta access updates and launch follow-up.
                </Typography>
              </Grid>

              <Grid item xs={12} md={5.6}>
                <GlassPanel sx={{ p: 2.2 }}>
                  <Stack spacing={1.4}>
                  {[
                      ['Start on web today', 'Create your account in a few quick steps and begin tracking right away.'],
                      ['Help shape mobile', 'Beta testers get an early look at the iPhone app and help improve the launch.'],
                      ['One FastTime account', 'Your progress, billing, and settings stay connected across web and mobile.'],
                    ].map(([title, body]) => (
                      <Box key={title} sx={{ p: 1.8, borderRadius: 2, border: `1px solid ${tokens.border}`, bgcolor: 'rgba(255,255,255,0.03)' }}>
                        <Typography sx={{ color: tokens.cream, fontSize: '0.98rem', fontWeight: 600, mb: 0.35 }}>{title}</Typography>
                        <Typography sx={{ color: tokens.textSoft, fontSize: '0.88rem', lineHeight: 1.65 }}>{body}</Typography>
                      </Box>
                    ))}
                  </Stack>
                </GlassPanel>
              </Grid>
            </Grid>
          </FadeSection>
        </Container>
      </Box>

      <Box id="cta" sx={{ pb: { xs: 8, md: 10 } }}>
        <Container maxWidth="lg" sx={{ px: { xs: 2, md: 4 } }}>
          <FadeSection>
            <GlassPanel
              sx={{
                p: { xs: 3, md: 4.5 },
                textAlign: 'center',
                backgroundImage: `linear-gradient(180deg, rgba(11,20,16,0.72), rgba(11,20,16,0.84)), url(${ctaBackground})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
              }}
            >
              <Typography variant="h2" sx={{ color: tokens.cream, fontSize: { xs: '2.35rem', md: '3.5rem' }, mb: 1.2 }}>
                Ready to start with FastTime?
              </Typography>
              <Typography sx={{ color: tokens.textSoft, maxWidth: 620, mx: 'auto', mb: 3.2 }}>
                Build a better routine with guided fasting, daily tracking, and progress you can keep up with.
              </Typography>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.6} justifyContent="center">
                <Button
                  variant="contained"
                  size="large"
                  onClick={() => navigate(user ? '/dashboard' : '/signup')}
                  sx={{ bgcolor: tokens.accent, color: tokens.bg, px: 3.6, py: 1.8 }}
                >
                  Get started for free
                </Button>
                {!user && (
                  <Button variant="outlined" size="large" onClick={() => navigate('/login')} sx={{ px: 3.2, py: 1.8 }}>
                    I already have an account
                  </Button>
                )}
              </Stack>
            </GlassPanel>
          </FadeSection>
        </Container>
      </Box>
    </Box>
  );
}
